import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { isEmpty, isNull } from 'lodash';
import Typography from '@material-ui/core/Typography';
import cn from 'classnames';
import { TRAVEL_PRICE_KEYS } from '../../../constants/travelCalcConstants';
import getFullNameByLang from '../../../helpers/langHelpers';
import { addPromoCode, removePromoCode } from '../../../redux/modules/travelContract';
import SelectedPlanPrices from '../SelectedPlanPrices';
import PromoCodeNotFound from '../PromoCodeNotFound';
import useDeviceResize from '../../../hooks/useDeviceResize';
import MobileComponents from './MobileComponents';
import DesktopComponents from './DesctopComponents/DesktopComponents';

import useStyles from './makeStyles';

const Member = ({
  traveler,
  promoCode,
  discountedPrice,
  isFetchingPrice,
  prices,
  selectedPlan,
  errorMessage,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const isDeviceLgSize = useDeviceResize('lg');
  const [newPromoCode, setNewPromoCode] = useState(
    !isEmpty(promoCode) ? promoCode.policyNumber : '',
  );
  const [showPromoInput, setShowPromoInput] = useState(true);

  const submitPromoCode = useCallback(() => {
    if (newPromoCode) {
      setShowPromoInput(false);

      const { memberId, passport, socCard } = traveler;
      dispatch(removePromoCode(memberId));

      dispatch(
        addPromoCode({
          memberId,
          passport,
          socialCard: socCard,
          policyNumber: newPromoCode,
        }),
      );
    }
  }, [dispatch, newPromoCode, traveler]);

  const getPricePerTraveler = useCallback(
    (passport) => {
      if (prices[selectedPlan] && prices[selectedPlan][passport]) {
        return prices[selectedPlan][passport][TRAVEL_PRICE_KEYS.SUM];
      }
      return null;
    },
    [prices, selectedPlan],
  );

  const removeAppliedPromoCode = useCallback(() => {
    dispatch(removePromoCode(traveler.memberId));
    setNewPromoCode('');
    setShowPromoInput(true);
  }, [dispatch, traveler.memberId]);

  return (
    <Grid
      container
      className={cn({
        [classes.root]: isDeviceLgSize,
      })}
    >
      <Grid item xl={6} xs={!isEmpty(promoCode) ? 8 : 4}>
        <Typography component="div" className={classes.memberName}>
          {getFullNameByLang(traveler, i18n.language) || t('travelContract.newMember')}
        </Typography>
      </Grid>
      {!isDeviceLgSize ? (
        <DesktopComponents
          setNewPromoCode={setNewPromoCode}
          removeAppliedPromoCode={removeAppliedPromoCode}
          setShowPromoInput={setShowPromoInput}
          submitPromoCode={submitPromoCode}
          showPromoInput={showPromoInput}
          newPromoCode={newPromoCode}
          promoCode={promoCode}
        />
      ) : (
        <MobileComponents
          submitPromoCode={submitPromoCode}
          setNewPromoCode={setNewPromoCode}
          removeAppliedPromoCode={removeAppliedPromoCode}
          setShowPromoInput={setShowPromoInput}
          showPromoInput={showPromoInput}
          newPromoCode={newPromoCode}
          promoCode={promoCode}
        />
      )}
      <Grid item xs={12} className={classes.discountedPrice}>
        {!isNull(errorMessage) && <PromoCodeNotFound errorMessage={errorMessage} />}
        <SelectedPlanPrices
          discountedPrice={discountedPrice}
          price={getPricePerTraveler(traveler.passport)}
          isFetchingPrice={isFetchingPrice}
        />
      </Grid>
    </Grid>
  );
};

Member.propTypes = {
  traveler: PropTypes.shape({
    memberId: PropTypes.number,
    passport: PropTypes.string,
    socCard: PropTypes.string,
  }).isRequired,
  promoCode: PropTypes.shape({
    policyNumber: PropTypes.string,
  }),
  prices: PropTypes.shape({}).isRequired,
  discountedPrice: PropTypes.shape({}),
  isFetchingPrice: PropTypes.bool.isRequired,
  selectedPlan: PropTypes.string.isRequired,
  errorMessage: PropTypes.shape({}),
};

Member.defaultProps = {
  errorMessage: null,
  promoCode: {},
  discountedPrice: null,
};

export default Member;
